import { useState, forwardRef, useMemo } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const LEARN_OPTIONS = [
  { label: "Polish", code: "PL", flag: "🇵🇱" },
  { label: "Spanish", code: "ES", flag: "🇪🇸" },
  { label: "German", code: "DE", flag: "🇩🇪" },
  { label: "French", code: "FR", flag: "🇫🇷" },
  { label: "Italian", code: "IT", flag: "🇮🇹" },
  { label: "Portuguese", code: "PT", flag: "🇵🇹" },
  { label: "Dutch", code: "NL", flag: "🇳🇱" },
  { label: "Japanese", code: "JP", flag: "🇯🇵" },
  { label: "Korean", code: "KR", flag: "🇰🇷" },
  { label: "Mandarin", code: "CN", flag: "🇨🇳" },
];

const USE_OPTIONS = [
  { label: "English", code: "GB", flag: "🇬🇧" },
  { label: "Dutch", code: "NL", flag: "🇳🇱" },
  { label: "German", code: "DE", flag: "🇩🇪" },
];

function FlagCheckboxGrid({ name, options, value, onChange, allowOther }) {
  const [otherText, setOtherText] = useState("");
  const otherSelected = value.includes("Other");

  const toggle = (label) => {
    onChange(value.includes(label) ? value.filter((v) => v !== label) : [...value, label]);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {options.map(({ label, code, flag }) => {
          const checked = value.includes(label);
          return (
            <label
              key={label}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition select-none
                ${checked
                  ? "bg-[var(--vb-primary-soft)] border-[var(--vb-primary)] text-white"
                  : "bg-[var(--vb-bg)] border-[var(--vb-border)] text-[var(--vb-muted)] hover:border-[var(--vb-border-strong)]"
                }`}
            >
              <span
                className={`w-4 h-4 rounded flex items-center justify-center border text-[10px] shrink-0 transition
                  ${checked
                    ? "bg-[var(--vb-primary)] border-[var(--vb-primary)] text-[var(--vb-bg-deep)]"
                    : "border-[var(--vb-border-strong)]"
                  }`}
              >
                {checked && <Check size={10} strokeWidth={3} />}
              </span>
              <input
                type="checkbox"
                name={name}
                value={label}
                checked={checked}
                onChange={() => toggle(label)}
                className="sr-only"
              />
              <img
                src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
                alt={`${label} flag`}
                className="w-5 h-3.5 object-cover rounded-[2px] shadow-sm border border-[var(--vb-border)] shrink-0"
              />
              <span className="truncate">{label}</span>
            </label>
          );
        })}
        {allowOther && (
          <label
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition select-none
              ${otherSelected
                ? "bg-[var(--vb-primary-soft)] border-[var(--vb-primary)] text-white"
                : "bg-[var(--vb-bg)] border-[var(--vb-border)] text-[var(--vb-muted)] hover:border-[var(--vb-border-strong)]"
              }`}
          >
            <span
              className={`w-4 h-4 rounded flex items-center justify-center border text-[10px] shrink-0 transition
                ${otherSelected
                  ? "bg-[var(--vb-primary)] border-[var(--vb-primary)] text-[var(--vb-bg-deep)]"
                  : "border-[var(--vb-border-strong)]"
                }`}
            >
              {otherSelected && <Check size={10} strokeWidth={3} />}
            </span>
            <input
              type="checkbox"
              name={name}
              value="Other"
              checked={otherSelected}
              onChange={() => toggle("Other")}
              className="sr-only"
            />
            <span className="text-base leading-none">🌐</span>
            <span className="truncate">Other</span>
          </label>
        )}
      </div>
      {allowOther && otherSelected && (
        <input
          type="text"
          name={`${name}-other`}
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Which language?"
          className="mt-2 w-full bg-[var(--vb-bg)] border border-[var(--vb-border)] text-white px-4 py-2 rounded-lg placeholder:text-white/30 focus:outline-none focus:border-[var(--vb-primary)] transition text-sm"
        />
      )}
    </div>
  );
}

function Stepper({ activeStep }) {
  const steps = ["Email", "Languages to learn", "Languages you use"];

  return (
    <div className="flex flex-col items-center gap-0 pt-1">
      {steps.map((label, i) => {
        const isComplete = i < activeStep;
        const isCurrent = i === activeStep;
        return (
          <div key={label} className="flex flex-col items-center">
            {/* Dot */}
            <div
              className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-500
                ${isComplete
                  ? "bg-[var(--vb-primary)] border-[var(--vb-primary)] text-[var(--vb-bg-deep)]"
                  : isCurrent
                    ? "bg-[var(--vb-surface)] border-[var(--vb-primary)] text-[var(--vb-primary)]"
                    : "bg-[var(--vb-surface)] border-[var(--vb-border)] text-[var(--vb-muted)]"
                }`}
            >
              {isComplete ? <Check size={14} strokeWidth={3} /> : i + 1}
            </div>
            {/* Label */}
            <span
              className={`text-[11px] font-mono mt-1 whitespace-nowrap transition-colors duration-300
                ${isCurrent ? "text-[var(--vb-primary)]" : isComplete ? "text-[var(--vb-text)]" : "text-[var(--vb-muted)]"}`}
            >
              {label}
            </span>
            {/* Line */}
            {i < steps.length - 1 && (
              <div className="relative w-0.5 h-8 my-1">
                <div className="absolute inset-0 bg-[var(--vb-border)]" />
                <div
                  className={`absolute top-0 left-0 w-full bg-[var(--vb-primary)] transition-all duration-500 ${
                    isComplete ? "h-full" : "h-0"
                  }`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const WaitlistCTA = forwardRef(({ id = "waitlist" }, ref) => {
  const [email, setEmail] = useState("");
  const [learnLangs, setLearnLangs] = useState([]);
  const [useLangs, setUseLangs] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);

  const activeStep = useMemo(() => {
    if (useLangs.length > 0) return 3;
    if (learnLangs.length > 0) return 2;
    if (emailValid) return 1;
    return 0;
  }, [emailValid, learnLangs, useLangs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Email required");
      return;
    }
    if (learnLangs.length === 0) {
      toast.error("Select at least one language you want to learn");
      return;
    }
    if (useLangs.length === 0) {
      toast.error("Select at least one language you use");
      return;
    }

    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
    if (!accessKey) {
      toast.error("Form error: REACT_APP_WEB3FORMS_ACCESS_KEY is not configured.");
      console.error("To capture submissions, please set the REACT_APP_WEB3FORMS_ACCESS_KEY environment variable in your Cloudflare Pages dashboard.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          access_key: accessKey,
          email: email,
          "learn-languages": learnLangs.join(", "),
          "use-languages": useLangs.join(", "),
          subject: "New Waitlist Signup - Verbatim"
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        toast.success("You're on the list!");
      } else {
        toast.error(result.message || "Something went wrong. Try again.");
      }
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id={id}
      ref={ref}
      data-testid="waitlist-section"
      className="relative py-24 sm:py-28 border-t border-[var(--vb-border)]"
    >
      <div className="container-x">
        <div className="card-soft relative p-8 sm:p-14 overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-30" aria-hidden />
          <div
            className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-[var(--vb-primary)]/10 blur-3xl"
            aria-hidden
          />

          <div className="relative flex flex-col lg:flex-row gap-12 items-start">
            {/* Left: copy */}
            <div className="flex-1 min-w-0">
              <div className="mono-label mb-4">[ Join the waitlist ]</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
                Get in line.
                <br />
                <span className="text-[var(--vb-primary)]">Start reading.</span>
              </h2>
              <p className="mt-5 text-[var(--vb-muted)] text-base max-w-xl">
                Each week new readers from the waitlist get added. Drop your
                details — we'll send your invite the day your seat opens, with a
                starter story in your target language already loaded.
              </p>
              <div
                className="mt-6 text-xs font-mono text-[var(--vb-muted)]"
                data-testid="waitlist-counter"
              >
                no credit card · no spam · no mails
              </div>
            </div>

            {/* Right: form */}
            <div className="flex-1 min-w-0 w-full">
              {submitted ? (
                <div
                  className="bg-[var(--vb-bg)]/60 border border-[var(--vb-border)] rounded-xl p-6"
                  data-testid="waitlist-success"
                >
                  <div className="mono-label text-[var(--vb-primary)] mb-2">
                    You're in
                  </div>
                  <div className="font-display text-2xl font-extrabold">
                    We'll be in touch.
                  </div>
                  <p className="mt-2 text-[var(--vb-muted)] text-sm">
                    Confirmation sent to{" "}
                    <span className="text-white">{email}</span>.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="waitlist-form"
                >
                  <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} />

                  <div className="flex gap-5">
                    {/* Stepper */}
                    <Stepper activeStep={activeStep} />

                    {/* Fields */}
                    <div className="flex-1 space-y-6">
                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-[var(--vb-muted)] mb-1.5">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          data-testid="waitlist-email-input"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@somewhere.com"
                          className="w-full bg-[var(--vb-bg)] border border-[var(--vb-border)] text-white px-4 py-2.5 rounded-lg placeholder:text-white/30 focus:outline-none focus:border-[var(--vb-primary)] transition text-sm"
                        />
                      </div>

                      {/* Learn languages */}
                      <div>
                        <label className="block text-sm font-medium text-[var(--vb-muted)] mb-1.5">
                          I want to learn this/these language(s)
                        </label>
                        <FlagCheckboxGrid
                          name="learn-languages"
                          options={LEARN_OPTIONS}
                          value={learnLangs}
                          onChange={setLearnLangs}
                          allowOther
                        />
                      </div>

                      {/* Use languages */}
                      <div>
                        <label className="block text-sm font-medium text-[var(--vb-muted)] mb-1.5">
                          Using this/these language(s)
                        </label>
                        <FlagCheckboxGrid
                          name="use-languages"
                          options={USE_OPTIONS}
                          value={useLangs}
                          onChange={setUseLangs}
                          allowOther
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={loading}
                          data-testid="waitlist-submit"
                          className="btn-primary justify-center disabled:opacity-60"
                        >
                        {loading ? (
                          <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                        ) : (
                          <ArrowRight size={16} />
                        )}
                          Reserve my spot
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

WaitlistCTA.displayName = "WaitlistCTA";